import { Modal, TextArea } from "@appkit4/react-components";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Button,
  Checkbox,
  InputNumber,
} from "../../components/ReadonlyAwareInputs";
import {
  getGroupData,
  getPoaIndex,
  getSelectedPeriod,
} from "../../services/GroupContext";
import "./outputs.scss";

import { validReturnTypes } from "../GroupPOA/GroupPoAUtils";
import ConfirmationModal from "./ConfirmationModal";

const getNumOfCompaniesInitialValue = (returnStatus) => {
  const poaIndex = getPoaIndex();
  const groupData = getGroupData();

  if (returnStatus?.num_companies_worldwide_group) {
    return returnStatus?.num_companies_worldwide_group;
  } else {
    if (poaIndex > 0)
      return (
        groupData?.periods_of_account[poaIndex - 1].return_status
          ?.num_companies_worldwide_group ?? ""
      );
    else return "";
  }
};

const ReturnModal = ({
  type,
  visible,
  onCancel,
  returnStatus,
  save,
  download,
  isLoading,
}) => {
  const [numOfCompanies, setNumOfCompanies] = useState(
    getNumOfCompaniesInitialValue(returnStatus),
  );
  const selectedPoA = getSelectedPeriod();
  const [hasEstimates, setHasEstimates] = useState(
    selectedPoA?.angie_estimated || (returnStatus?.contains_estimates ?? false),
  );
  const [estimationDetailsComment, setEstimationDetailsComment] = useState(
    returnStatus.details_of_estimation ?? "",
  );
  const [natureOfRevision, setNatureOfRevision] = useState(
    returnStatus.nature_of_revision ?? "",
  );
  const [isError, setIsError] = useState(false);

  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  const angieEstimated = selectedPoA?.angie_estimated;

  useEffect(() => {
    let hasErrors = !numOfCompanies;

    if (hasEstimates) {
      hasErrors = hasErrors || !estimationDetailsComment;
    }

    if (returnStatus?.isRevised) {
      hasErrors = hasErrors || !natureOfRevision;
    }

    setIsError(hasErrors);
  }, [
    numOfCompanies,
    hasEstimates,
    estimationDetailsComment,
    returnStatus,
    natureOfRevision,
  ]);

  const reset = () => {
    const latestReturnStatus = selectedPoA?.return_status;

    setNumOfCompanies(getNumOfCompaniesInitialValue(latestReturnStatus));
    setHasEstimates(
      selectedPoA?.angie_estimated ||
        (latestReturnStatus.contains_estimates ?? false),
    );
    setEstimationDetailsComment(latestReturnStatus.details_of_estimation ?? "");
    setNatureOfRevision(latestReturnStatus.nature_of_revision ?? "");
  };

  const close = () => {
    reset();
    onCancel();
  };

  const onSave = async (
    code = undefined,
    timestamp = undefined,
    isFinalised = undefined,
    withoutReportingCompany = undefined,
  ) => {
    await save(
      {
        ...returnStatus,
        isRevisionCharged: returnStatus?.isRevised,
        type:
          type === "full"
            ? validReturnTypes.full
            : validReturnTypes.abbreviated,
        num_companies_worldwide_group: numOfCompanies,
        contains_estimates: hasEstimates,
        details_of_estimation: hasEstimates ? estimationDetailsComment : "",
        nature_of_revision: natureOfRevision,
        ...(code && { code }),
        ...(timestamp && { timestamp }),
        ...(isFinalised && { isFinalised }),
        ...(withoutReportingCompany !== undefined && {
          withoutReportingCompany,
        }),
      },
      type,
    );
  };

  const onGenerateOutput = async () => {
    const timestamp = new Date().toISOString();
    const code = uuidv4();
    await onSave(code, timestamp, true, false);

    download();

    setIsConfirmModalVisible(false);
    onCancel();
  };

  return (
    <>
      <Modal
        initialFocus={false}
        visible={visible}
        title={
          type === "full"
            ? "Generate Full Return"
            : "Generate Abbreviated return"
        }
        ariaLabel={"Return Modal"}
        onCancel={close}
        maskCloseable={false}
        modalStyle={{ width: "33.75rem" }}
        footer={
          <>
            <Button neverReadonly onClick={close} kind="secondary">
              Cancel
            </Button>
            <Button
              neverReadonly
              loading={isLoading}
              disabled={isError}
              onClick={() => onSave()}
              style={{ marginRight: "8px" }}
              kind="secondary"
            >
              Save
            </Button>
            <Button
              neverReadonly
              disabled={isError}
              onClick={() => setIsConfirmModalVisible(true)}
            >
              Generate Output
            </Button>
          </>
        }
        bodyStyle={{ minHeight: "300px" }}
      >
        <div className="modal-grid">
          <InputNumber
            enabledWhenFinalised
            title="Number of companies in the worldwide group:"
            required={true}
            value={numOfCompanies}
            onChange={(value) =>
              value > 0
                ? setNumOfCompanies(Math.round(value))
                : setNumOfCompanies("")
            }
          />
          <div className="flex-container">
            <label>Does this return contain estimates?</label>
            <Checkbox
              enabledWhenFinalised
              disabled={angieEstimated}
              checked={hasEstimates}
              onChange={(value) => setHasEstimates(value)}
            />
          </div>
          {hasEstimates && (
            <TextArea
              enabledWhenFinalised
              title="Please comment on the details of the estimation"
              required={true}
              value={estimationDetailsComment}
              onChange={(value) => setEstimationDetailsComment(value)}
            />
          )}
          {angieEstimated && (
            <>
              <p style={{ fontSize: "0.8rem" }}>
                If you are including an estimate of ANGIE, some suggested
                wording to cover this is as follows - it may need to be amended
                to reflect the specific facts:
              </p>
              <p style={{ fontSize: "0.8rem" }}>
                The Adjusted Net Group Interest Expense (ANGIE) included in the
                return has been estimated. Given that the group is relying on
                the [£2m de-minimis for the period/30% of Aggregate UK
                Tax-EBITDA], the ANGIE is not the limiting factor. The estimated
                information will not be updated by [XX/XX/XX] as required in
                Para 27, Schedule 7A on the basis that ANGIE is not the limiting
                factor and, as such, does not impact on the overall CIR
                calculations. We understand that in these circumstances, in line
                with the Guidance at CFM98540, HMRC will not seek to levy a
                penalty under Para 27(5).
              </p>
            </>
          )}
          {returnStatus?.isRevised && (
            <TextArea
              enabledWhenFinalised
              title="Please describe the nature of the revision to the original return"
              required={true}
              value={natureOfRevision}
              onChange={(value) => setNatureOfRevision(value)}
            />
          )}
        </div>
      </Modal>
      <ConfirmationModal
        visible={isConfirmModalVisible}
        isLoading={isLoading}
        onCancel={() => setIsConfirmModalVisible(false)}
        onClick={onGenerateOutput}
      />
    </>
  );
};

export default ReturnModal;
